import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, Input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { take } from 'rxjs';
import { PhoneCallButtonComponent } from '../../basic/buttons/phone-call-button/phone-call-button.component';
import { PrimaryButtonComponent } from '../../basic/buttons/primary-button/primary-button.component';
import { getDeepgramVoiceById } from '../../basic/deepgram/deepgram';
import { PersonalityService } from '../../core/personality/personality.service';
import { getResumeSourceColor, getResumeSourceName, Resume } from '../../core/resume/resume';
import { ResumeService } from '../../core/resume/resume.service';
import { SessionService } from '../../core/session/session.service';
import { EditResumeComponent } from '../resume/edit-resume/edit-resume.component';
import { ViewResumeDetailsComponent } from '../resume/view-resume-details/view-resume-details.component';
import { NewEditSessionTemplateComponent } from '../session-template/new-edit-session-template/new-edit-session-template.component';
import { SessionTemplateService } from './../../core/session-template/session.template.service';

@Component({
  selector: 'app-profile-card',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,

    NzButtonModule,
    NzIconModule,
    NzTagModule,
    NzBadgeModule,

    NewEditSessionTemplateComponent,
    EditResumeComponent,
    PrimaryButtonComponent,
    PhoneCallButtonComponent,
    ViewResumeDetailsComponent,
  ],
  templateUrl: './profile-card.component.html',
  styleUrl: './profile-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileCardComponent {
  @Input({ required: true }) resume?: Resume;

  isEditResumeVisible = false;

  isViewResumeDetailsVisible = false;

  personalityList = toSignal(this.personalityService.itemList$(), { initialValue: [] });

  resumeChanged = toSignal(this.resumeService.resumeChanged$, { initialValue: 0 });

  selectedPersonalityList = computed(() => {
    this.resumeChanged();

    return this.resumeService.getPersonalityList(this.resume)();
  });

  getDeepgramVoiceById() {
    return getDeepgramVoiceById(this.resume?.subData.content?.voice);
  }

  showDetailsResume?: Resume;

  constructor(
    private sessionService: SessionService,
    private resumeService: ResumeService,
    private sessionTemplateService: SessionTemplateService,
    private messageService: NzMessageService,
    private personalityService: PersonalityService,
    private router: Router
  ) {}

  getResumeSourceName() {
    return getResumeSourceName(this.resume);
  }

  getResumeSourceColor() {
    return getResumeSourceColor(this.resume);
  }

  onViewDetailsClick(event: Event) {
    event.stopPropagation();

    this.showDetailsResume = this.resume;

    this.isViewResumeDetailsVisible = true;
  }

  onViewResumeDetailsVisibleChange(isVisible: boolean) {
    if (isVisible === false) {
      this.showDetailsResume = undefined;
    }
  }

  onDeleteProfileClick(event: Event) {
    event.stopPropagation();

    this.sessionTemplateService
      .itemListByResumeId$(this.resume!.id)
      .pipe(take(1))
      .subscribe((data) => {
        if (data.length === 0) {
          this.resumeService
            .deleteResumeList$([this.resume!])
            .pipe(take(1))
            .subscribe(() => {});
        } else {
          this.messageService.error(
            `Please delete ${data.length} related scenarios to proceed with deleting this customer.`,
            { nzDuration: 10000 }
          );
        }
      });
  }

  onEditProfileClick(event: Event) {
    event.stopPropagation();
    this.isEditResumeVisible = true;
  }

  onCopyProfileClick(event: Event) {
    this.resumeService
      .addResume$({ ...this.resume!.subData }, this.resume?.type)
      .pipe(take(1))
      .subscribe(() => {});
  }
}
